import Agency from "@/models/SvcModels/Agency";
import AgencyGroup from "@/models/SvcModels/AgencyGroup";
import User from "@/models/User";
import { defineStore } from "pinia";
import { useRepo } from "pinia-orm";
import { agenciesApi, userManagementUsersApi, fetchAll } from "@/api/userManagementClient";
import { userApi } from "@/api/userClient";
import { useOidcStore } from "vue3-oidc";
import { useContributorStore } from "@/store/modules/contributor";

const UserRepo = useRepo(User);
const AgencyRepo = useRepo(Agency);
const AgencyGroupRepo = useRepo(AgencyGroup);

export const useUserStore = defineStore("user", {
  state: () => ({}),

  actions: {
    async initializeCurrentUser() {
      const user = await this.fetchCurrentUser();
      if (!user?.usermanagementUuid) {
        return;
      }
      const agencyResult = await this.fetchUserAgencyAndAgencyGroup({
        uuid: user.usermanagementUuid,
      });

      const userManagement = await this.retrieveUserUsermanagementUuid(user.usermanagementUuid);

      if (!agencyResult) {
        return;
      }
      UserRepo.where("isCurrentUser", true).update({
        agency: agencyResult.uuid,
        telephoneNumber: userManagement?.telephoneNumber,
      });
    },

    async fetchUserAgencyAndAgencyGroup({ uuid }) {
      try {
        const agency = await agenciesApi.retrieveUserAgency({ userUuid: uuid });
        if (!agency?.uuid) {
          return;
        }

        if (agency.agencyGroup) {
          AgencyGroupRepo.save(agency.agencyGroup);
          agency.agencyGroup = agency.agencyGroup.uuid;
        }
        return AgencyRepo.save(agency);
      } catch (err) {
        console.log(err);
      }
    },

    async fetchCurrentUser() {
      const oidcStore = useOidcStore();
      const user = oidcStore.state.value.user;
      if (!user?.profile?.usermanagementUuid) {
        return null;
      }
      try {
        const response = await userApi.byUsermanagementUuid({
          usermanagementUuid: user.profile.usermanagementUuid,
        });
        return UserRepo.save({ ...response, isCurrentUser: true });
      } catch (err) {
        console.error(err);
        return null;
      }
    },

    async retrieveUserUsermanagementUuid(uuid) {
      try {
        const user = await userManagementUsersApi.retrieve({ uuid });
        UserRepo.save(user);
        return user;
      } catch (err) {
        console.error(err);
        return null;
      }
    },

    async fetchAgencyByCode(code) {
      try {
        const agency = await agenciesApi.retrieveByCode({ code });

        if (!agency?.uuid) {
          throw new Error("There is no agency with this code");
        }

        AgencyRepo.save(agency);

        return agency;
      } catch (err) {
        console.error(err);
        throw err;
      }
    },

    async fetchAgencyByUuid(uuid) {
      try {
        const agency = await agenciesApi.retrieve({ uuid });

        if (!agency?.uuid) {
          throw new Error("There is no agency with this uuid");
        }

        AgencyRepo.save(agency);

        return agency;
      } catch (err) {
        console.error(err);
        throw err;
      }
    },

    async searchUser(params) {
      const response = await userManagementUsersApi.list(params);
      const users = response.results || [];
      return users.map((user) => new User(user));
    },

    async fetchAgencies(params = {}) {
      const { results } = await fetchAll(
        agenciesApi,
        "list",
        {
          ...params,
          applicationName: import.meta.env.VITE_APP_SSP_SERVICE_ID,
        },
        Infinity,
      );

      AgencyRepo.save(results);

      return { results: results.map((agency) => new Agency(agency)) };
    },

    async updateUserProfile({ uuid, signature }) {
      const response = await userApi.partialUpdate({
        uuid,
        patchedUserRequest: { signature },
      });
      return UserRepo.save(response);
    },
  },

  getters: {
    getCurrentUser: () => {
      return UserRepo.where("isCurrentUser", true)
        .with("agencyDatas", (query) => query.with("agencyGroupDatas"))
        .first();
    },

    isCurrentUserADeveloper: (state) => {
      return state.getCurrentUser?.isDev();
    },

    isCurrentUserAgencyManager: (state) => {
      const currentUser = state.getCurrentUser;
      return currentUser?.isAgencyGroupManager() || currentUser?.isAgencyManager();
    },

    isCurrentUserTheProjectLead: (state) => {
      const contributorStore = useContributorStore();
      return (
        contributorStore.getMissionProjectLead?.usermanagementUuid ===
        state.getCurrentUser?.usermanagementUuid
      );
    },

    isCurrentUserThePilot: (state) => {
      const contributorStore = useContributorStore();
      return (
        contributorStore.getMissionPilot?.usermanagementUuid ===
        state.getCurrentUser?.usermanagementUuid
      );
    },

    isCurrentUserTheProjectSupervisor: (state) => {
      const contributorStore = useContributorStore();
      return (
        contributorStore.getMissionSupervisor?.userEmail?.toLowerCase() ===
        state.getCurrentUser?.email?.toLowerCase()
      );
    },
    isCurrentUserMaterialManager: (state) => {
      return state.getCurrentUser?.isMaterialManager();
    },

    getUserByEmail: () => (email) => {
      return UserRepo.where("email", email).first();
    },

    getAgencyByUuid: () => (uuid) => {
      return AgencyRepo.where("uuid", uuid).first();
    },

    getAllAgencies: () => {
      return AgencyRepo.all();
    },
  },
});
